import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { useAuth } from '../auth/AuthProvider'
import type { DashboardUser } from '../auth/auth-adapter'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import {
  onboardingSchedulingQueryKey,
  onboardingSchedulingQueryOptions,
  onboardingStatusQueryOptions,
} from './onboarding-query'
import { destinationPath, routeOnboardingResponse } from './onboarding-routing'
import { hasHealthySchedulingSnapshot } from './onboarding-scheduling-contracts'
import { SchedulingSummary } from './SchedulingSummary'
import styles from './SchedulingPage.module.css'

export function SchedulingCheckPage({
  client = getOnboardingApiClient(),
}: {
  client?: OnboardingApiClient
}) {
  const auth = useAuth()
  if (auth.status !== 'authenticated') {
    return <Spinner label="Restoring your setup" />
  }
  return (
    <AuthenticatedSchedulingCheck
      key={auth.user.uid}
      client={client}
      user={auth.user}
    />
  )
}

function AuthenticatedSchedulingCheck({
  client,
  user,
}: {
  client: OnboardingApiClient
  user: DashboardUser
}) {
  const auth = useAuth()
  const location = useLocation()
  const statusQuery = useQuery(onboardingStatusQueryOptions(client, user))

  if (statusQuery.isPending) return <Spinner label="Checking your setup" />
  if (statusQuery.isError) {
    if (
      statusQuery.error instanceof OnboardingApiError &&
      statusQuery.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    return (
      <Card className={styles.panel}>
        <StatePanel
          kind="error"
          title="Scheduling setup could not be loaded"
          description="Retry to load the latest scheduling state. Your saved setup has not been changed."
        />
        <Button type="button" onClick={() => void statusQuery.refetch()}>
          Retry
        </Button>
      </Card>
    )
  }

  const destination = routeOnboardingResponse(statusQuery.data)
  if (destination !== 'scheduling') {
    return <Navigate to={destinationPath(destination)} replace />
  }
  return <SchedulingCheck client={client} user={user} onSignOut={auth.logout} />
}

function SchedulingCheck({
  client,
  user,
  onSignOut,
}: {
  client: OnboardingApiClient
  user: DashboardUser
  onSignOut: () => Promise<void>
}) {
  const queryClient = useQueryClient()
  const schedulingQuery = useQuery(
    onboardingSchedulingQueryOptions(client, user)
  )
  const [working, setWorking] = useState(false)
  const [feedback, setFeedback] = useState<'pending' | 'unavailable' | null>(
    null
  )

  if (schedulingQuery.isPending) {
    return <Spinner label="Checking your scheduling connection" />
  }
  if (schedulingQuery.isError) {
    return (
      <Card className={styles.panel}>
        <StatePanel
          kind="error"
          title="Scheduling setup could not be loaded"
          description="Retry to load the latest scheduling state. Your saved setup has not been changed."
        />
        <Button type="button" onClick={() => void schedulingQuery.refetch()}>
          Retry
        </Button>
      </Card>
    )
  }
  if (schedulingQuery.data.schemaVersion === 2) {
    return (
      <Pav1SchedulingCheck
        client={client}
        user={user}
        scheduling={schedulingQuery.data}
        onSignOut={onSignOut}
      />
    )
  }
  if (hasHealthySchedulingSnapshot(schedulingQuery.data)) {
    return <Navigate to="/onboarding/scheduling/booking" replace />
  }

  const discover = async () => {
    if (working) return
    setWorking(true)
    setFeedback(null)
    try {
      const response = await client.discoverScheduling(
        user,
        schedulingQuery.data.revision
      )
      queryClient.setQueryData(onboardingSchedulingQueryKey(user.uid), response)
    } catch (error) {
      setFeedback(
        error instanceof OnboardingApiError &&
          error.code === 'operation-in-progress'
          ? 'pending'
          : 'unavailable'
      )
      await schedulingQuery.refetch()
    } finally {
      setWorking(false)
    }
  }

  const snapshot = schedulingQuery.data.snapshot
  const health = snapshot?.resourceHealth
  return (
    <Card className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.eyebrow}>Scheduling check</span>
        <h1>Check your scheduling connection</h1>
        <p>
          Flio imports scheduling information from your connected NexHealth/PMS
          system. Refresh after changing provider schedules or appointment
          durations in your source system.
        </p>
      </div>

      {feedback === 'pending' && (
        <StatePanel
          title="A scheduling check is already running"
          description="Wait a moment, then retry to load the authoritative result."
        />
      )}
      {(feedback === 'unavailable' ||
        schedulingQuery.data.lastDiscoveryFailure) && (
        <StatePanel
          kind="error"
          title="Scheduling could not be refreshed"
          description="The prior successful check is preserved. Retry when NexHealth is available, or contact Flio support if this continues."
        />
      )}
      {health?.providers !== undefined && health.providers !== 'READY' && (
        <StatePanel
          kind="error"
          title="No usable providers found"
          description="Add or activate a provider in your source scheduling system, then refresh."
        />
      )}
      {health?.appointmentTypes !== undefined &&
        health.appointmentTypes !== 'READY' && (
          <StatePanel
            kind="error"
            title="No usable appointment types found"
            description="Add an appointment type with a positive duration in your source system, then refresh."
          />
        )}
      {health?.availabilities !== undefined &&
        health.availabilities !== 'READY' && (
          <StatePanel
            kind="error"
            title="No usable availability found"
            description="Add provider availability in your source scheduling system, then refresh."
          />
        )}
      {health?.providerTypeAssociations !== undefined &&
        health.providerTypeAssociations !== 'READY' && (
          <StatePanel
            kind="error"
            title="Provider and appointment type setup is incomplete"
            description="Associate at least one usable provider with an appointment type in your source system, then refresh."
          />
        )}
      {(health?.syncRead === 'RED' || health?.syncWrite === 'RED') && (
        <StatePanel
          title="NexHealth synchronization telemetry is degraded"
          description="Direct resource reads may still be available. Continue validation; booking enablement remains subject to separate provider write authority."
        />
      )}

      <SchedulingSummary scheduling={schedulingQuery.data} />

      <p className={styles.note}>
        Clinic hours define when Flio may offer appointments. Actual
        availability still depends on providers, appointment types, resources,
        and the current NexHealth/PMS schedule.
      </p>
      <div className={styles.actions}>
        <Button
          type="button"
          disabled={working}
          onClick={() => void discover()}
        >
          {working
            ? 'Checking…'
            : snapshot
              ? 'Refresh scheduling'
              : 'Check scheduling'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={working}
          onClick={() => void onSignOut()}
        >
          Sign out
        </Button>
      </div>
    </Card>
  )
}

function Pav1SchedulingCheck({
  client,
  user,
  scheduling,
  onSignOut,
}: {
  client: OnboardingApiClient
  user: DashboardUser
  scheduling: Extract<
    Awaited<ReturnType<OnboardingApiClient['getScheduling']>>,
    { schemaVersion: 2 }
  >
  onSignOut: () => Promise<void>
}) {
  const queryClient = useQueryClient()
  const [working, setWorking] = useState(false)
  const [advanced, setAdvanced] = useState(false)
  const [unavailable, setUnavailable] = useState(false)

  if (advanced) return <Navigate to="/onboarding/communications" replace />

  const continueSetup = async () => {
    if (working || !scheduling.completionReady) return
    setWorking(true)
    setUnavailable(false)
    try {
      await client.completeScheduling(user, scheduling.revision)
      await queryClient.invalidateQueries({
        queryKey: onboardingStatusQueryOptions(client, user).queryKey,
      })
      setAdvanced(true)
    } catch {
      setUnavailable(true)
    } finally {
      setWorking(false)
    }
  }

  return (
    <Card className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.eyebrow}>Scheduling resources</span>
        <h1>Confirm your connected scheduling resources</h1>
        <p>
          Flio uses the shared, reviewed NexHealth connection for this clinic.
          Provider resources remain read-only and live booking stays off.
        </p>
      </div>
      {scheduling.resourceStatus === 'READY' ? (
        <StatePanel
          title="Scheduling resources are ready"
          description="The current provider directory is available for informational Voice context. No appointment writes are enabled."
        />
      ) : (
        <StatePanel
          title="Scheduling resources are refreshing"
          description="Flio is refreshing this clinic through its server-owned provider connection. Retry shortly; no credential is required."
        />
      )}
      {unavailable ? (
        <StatePanel
          kind="error"
          title="Could not continue safely"
          description="Refresh the current resource state and try again."
        />
      ) : null}
      <dl className={styles.metrics}>
        <div>
          <dt>NexHealth read sync</dt>
          <dd>{scheduling.syncReadStatus}</dd>
        </div>
        <div>
          <dt>NexHealth write sync</dt>
          <dd>{scheduling.syncWriteStatus}</dd>
        </div>
        <div>
          <dt>Live booking</dt>
          <dd>Disabled</dd>
        </div>
      </dl>
      <div className={styles.actions}>
        <Button
          type="button"
          disabled={working || !scheduling.completionReady}
          onClick={() => void continueSetup()}
        >
          {working ? 'Continuing…' : 'Continue to communications'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={working}
          onClick={() => window.location.reload()}
        >
          Refresh status
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={working}
          onClick={() => void onSignOut()}
        >
          Sign out
        </Button>
      </div>
    </Card>
  )
}
